/**
 * Blog "You May Also Find Helpful" carousel
 * Desktop: N cards + arrows
 * Mobile: max 3 cards, 1.5 visible + orange dots
 */
class BlogHelpfulCarousel extends HTMLElement {
  #scrollRaf = 0;
  #metricsRaf = 0;

  connectedCallback() {
    this.viewport = this.querySelector('[data-bhc-viewport]');
    this.track = this.querySelector('[data-bhc-track]');
    this.slides = Array.from(this.querySelectorAll('[data-bhc-slide]'));
    this.prevBtn = this.querySelector('[data-bhc-prev]');
    this.nextBtn = this.querySelector('[data-bhc-next]');
    this.dots = Array.from(this.querySelectorAll('[data-bhc-dot]'));
    this.index = 0;

    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = Number(dot.dataset.bhcDot);
        if (!Number.isNaN(target)) this.goTo(target);
      });
    });

    this.viewport?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.#scheduleMetrics());
    if (this.viewport) this.resizeObserver.observe(this.viewport);

    this.#scheduleMetrics();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#scrollRaf);
    cancelAnimationFrame(this.#metricsRaf);
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getVisibleDesktop() {
    return Number(this.dataset.visibleDesktop || 3);
  }

  getMobileMax() {
    const styles = getComputedStyle(this);
    const max = Number.parseInt(styles.getPropertyValue('--bhc-mobile-max'), 10);
    return Number.isFinite(max) && max > 0 ? max : 3;
  }

  getMobileVisibleCount() {
    const styles = getComputedStyle(this);
    const visible = Number.parseFloat(styles.getPropertyValue('--bhc-visible-mobile')) || 1.5;
    return visible > 0 ? visible : 1.5;
  }

  getMobileSlideCount() {
    return Math.min(this.getMobileMax(), this.slides.length);
  }

  getMaxIndex() {
    if (this.isMobile()) {
      return Math.max(0, this.getMobileSlideCount() - 1);
    }
    return Math.max(0, this.slides.length - this.getVisibleDesktop());
  }

  getStep() {
    if (!this.slides.length || !this.track) return 0;
    const gap =
      Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    return this.slides[0].offsetWidth + gap;
  }

  go(delta) {
    this.goTo(this.index + delta);
  }

  goTo(target) {
    this.index = Math.min(this.getMaxIndex(), Math.max(0, target));
    const step = this.getStep();
    this.viewport?.scrollTo({ left: this.index * step, behavior: 'smooth' });
    this.updateControls();
  }

  #scheduleMetrics = () => {
    cancelAnimationFrame(this.#metricsRaf);
    this.#metricsRaf = requestAnimationFrame(() => {
      this.#setSlideMetrics();
      if (this.isMobile() && this.index > this.getMaxIndex()) {
        this.index = this.getMaxIndex();
        this.viewport?.scrollTo({ left: this.index * this.getStep() });
      }
      this.updateControls();
    });
  };

  #setSlideMetrics() {
    if (!this.viewport || !this.isMobile()) {
      this.style.removeProperty('--bhc-slide-width');
      return;
    }

    const styles = getComputedStyle(this);
    const padding = Number.parseFloat(styles.getPropertyValue('--bhc-mobile-padding')) || 0;
    const gap = Number.parseFloat(styles.getPropertyValue('--bhc-mobile-gap')) || 16;
    const visible = this.getMobileVisibleCount();
    const available = Math.max(0, this.viewport.clientWidth - padding);
    const slideWidth = Math.max(0, (available - gap * (visible - 1)) / visible);

    this.style.setProperty('--bhc-slide-width', `${slideWidth}px`);
  }

  #onScroll = () => {
    if (!this.isMobile() || !this.viewport) return;

    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      const step = this.getStep();
      if (!step) return;
      this.index = Math.min(this.getMaxIndex(), Math.max(0, Math.round(this.viewport.scrollLeft / step)));
      this.updateControls();
    });
  };

  updateControls() {
    const max = this.getMaxIndex();
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= max;

    const mobileMax = this.isMobile() ? this.getMobileMax() : this.dots.length;
    this.dots.forEach((dot, i) => {
      if (i >= mobileMax) return;
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('blog-helpful-carousel')) {
  customElements.define('blog-helpful-carousel', BlogHelpfulCarousel);
}
